import { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation } from 'react-router'

// Actions
import { actions as labelsActions } from 'modules/labels/actions'

// Components
import { Layout, PageHeading } from '@tracklib/kit'
import SEO from 'components/base/seo'
import LabelsList from 'components/labelslist'
import { LabelsFilter } from 'components/filter'
import LoadMoreButton from 'components/base/loadmorebutton'

// Hooks
import { useRequest } from 'modules/requests/hooks'

// Types
import { EntityState, Dispatch, GetState, Label, QueryResult, State } from 'types'

export const loadInitialData = () => async (dispatch: Dispatch, getState: GetState) => {
  const { search } = getState().router.location
  return dispatch(labelsActions.fetchLabels(search))
}

const Labels = (): JSX.Element => {
  const dispatch = useDispatch()
  const { search } = useLocation()
  const searchInfo = useSelector<State, QueryResult>(state => state.labelsSearch[search || 'default'])
  const allLabels = useSelector<State, EntityState<Label>>(state => state.entities.labels)
  const labels = searchInfo?.ids ? searchInfo?.ids.map(id => allLabels[String(id)]) : []
  const fetchLabelsRequest = useRequest('FETCH_LABELS', search)

  useEffect(() => {
    if (!fetchLabelsRequest.isLoading && !fetchLabelsRequest.hasError && labels.length === 0) {
      dispatch(labelsActions.fetchLabels(search))
    }
  }, [search]) // eslint-disable-line

  const showLoadMoreButton = labels.length > 0 && labels.length < searchInfo.count
  return (
    <>
      <SEO title="Labels | Tracklib" />
      <PageHeading heading="Labels" description="Dig through our diverse collection of labels from all genres." />
      <Layout.FullWidth>
        <LabelsFilter />
      </Layout.FullWidth>
      <LabelsList labels={labels} fetchLabelsRequest={fetchLabelsRequest} />
      {showLoadMoreButton && (
        <LoadMoreButton
          disabled={fetchLabelsRequest.isLoading}
          onClick={() => dispatch(labelsActions.fetchLabels(search, searchInfo.lastPageLoaded + 1))}
          datatesting="button_labels_load_more"
        >
          Load more labels
        </LoadMoreButton>
      )}
    </>
  )
}

export default Labels
